/**
 * Plain-text and link builders for sharing a System Card edition: the credit
 * line, the text a reader copies out of a card or table, and the LinkedIn and
 * mailto targets.
 *
 * Pure and cross-compatible (no "use client"): the edition pages render on the
 * server and the share row runs in the browser, and both import from here.
 */
import type { DataTable, SystemCardEdition } from "@/constants/systemCard.types";

/**
 * Canonical absolute URL of an edition, optionally deep-linked to a section anchor.
 * Always production on purpose: a link copied from a preview or staging build
 * must still send readers to getlev.co, unlike `learnSystemCardUrl`, which
 * follows the deploy's origin for canonical metadata.
 */
export function systemCardUrl(slug: string, anchor?: string): string {
  return `https://getlev.co/learn/system-card/${slug}${anchor ? `#${anchor}` : ""}`;
}

/** "October 2026" from "2026-10". */
export function systemCardMonth(slug: string): string {
  const [y, m] = slug.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

/** Appends the edition's credit line ({month}, {url} substituted) to a block of plain text. */
export function withShareCredit(text: string, edition: SystemCardEdition, url: string): string {
  const month = systemCardMonth(edition.slug);
  // Function replacers: a string replacement would expand `$&` and friends inside the url.
  const credit = edition.shareCredit.replaceAll("{month}", () => month).replaceAll("{url}", () => url);
  return `${text.trimEnd()}\n\n${credit}`;
}

const NAMED_ENTITIES: Record<string, string> = {
  lt: "<",
  gt: ">",
  quot: '"',
  "#39": "'",
  nbsp: " ",
};

/** Strips tags (keeping link text) and decodes the few entities the data uses. */
export function htmlToPlainText(html: string): string {
  return (
    html
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<[^>]*>/g, "")
      .replace(/&(lt|gt|quot|#39|nbsp);/g, (_, name: string) => NAMED_ENTITIES[name])
      // &amp; decodes last, so "&amp;lt;" stays the literal text "&lt;".
      .replace(/&amp;/g, "&")
      .replace(/\s+/g, " ")
      .trim()
  );
}

/** A DataTable as aligned plain text (header, then one row per line), for the price map's Copy action. */
export function tableToPlainText(table: DataTable): string {
  const lines = [table.columns, ...table.rows.map((row) => row.map((cell) => htmlToPlainText(cell.html)))];
  const widths = table.columns.map((_, c) => Math.max(...lines.map((line) => (line[c] ?? "").length)));
  return lines
    .map((line) =>
      line
        .map((value, c) => (c === line.length - 1 ? value : value.padEnd(widths[c])))
        .join(" | ")
        .trimEnd(),
    )
    .join("\n");
}

export function linkedInShareUrl(url: string): string {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
}

export function systemCardMailto(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
